import {Provider} from "react-redux";
import {ThemeProvider} from "styled-components";
import {GlobalStyles, lightTheme} from "@/styles/ThemeConfig";
import Head from "next/head";
import {useStore} from "@/redux/store";
import {useRouter} from "next/router";
import Script from "next/script";
import dynamic from "next/dynamic";
import { useEffect } from "react";

const PopupScript = dynamic(() => import("@/services/PopupScript"), {
    ssr: false,
});

function App({ Component, pageProps }) {
    const store = useStore(pageProps.initialReduxState);
    const router = useRouter();

    useEffect(() => {
        if (typeof window === "undefined") return;
        const el = document.getElementById("embed_widget");
        if (el && window.TicketinghubWidget) {
            const already = el.querySelector('iframe#ticketinghub-embed-iframe');
            if (!already) {
                const cfg = window.TicketinghubWidget.widgetConfig(el);
                new window.TicketinghubWidget(el, cfg);
            }
        }
    }, [router.asPath]);

    useEffect(() => {
        const handleRouteChange = () => {
            if (typeof window !== "undefined" && window.oaiq && window.location.hostname.includes('secretfoodtours.com')) {
                window.oaiq('measure', 'page_viewed', {
                    type: 'contents',
                    contents: [{
                        id: window.location.pathname,
                        name: document.title,
                        content_type: 'page',
                    }],
                });
            }
        };

        router.events.on('routeChangeComplete', handleRouteChange);
        return () => router.events.off('routeChangeComplete', handleRouteChange);
    }, [router.events]);

    // Base URL from environment variables or fallback
    const baseURL = process.env.NEXT_PUBLIC_BASE_URL || "https://web.secrettours.com";
    const canonicalURL = `${baseURL}${router.asPath.split("?")[0]}`;

    // Metadata defaults
    const defaultMeta = {
        title: "Default Title",
        description: "Default description of the website.",
        image: "https://web.secrettours.com/images/meta_default_poster.jpg",
        createdAt: null,
        updatedAt: null,
    };

    const meta = { ...defaultMeta, ...pageProps.metadata };

    return (
        <Provider store={store}>
            <Head>
                {/* Dynamic Metadata */}
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.image} />
                <meta property="og:type" content="product" />
                <meta property="og:url" content={canonicalURL} />
                {meta.updatedAt && (
                    <meta property="og:updated_time" content={meta.updatedAt} />
                )}
                <meta name="twitter:title" content={meta.title} />
                <meta name="twitter:description" content={meta.description} />
                <meta name="twitter:image" content={meta.image} />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="preconnect" href="https://www.googletagmanager.com" />
                <link rel="preconnect" href="https://www.google-analytics.com" />

                {/* Static Metadata */}
                <link rel="icon" href="/favicon.ico" />

                <link rel="manifest" href="/site.webmanifest" />
                <meta name="msapplication-TileColor" content="#da532c" />
                <meta name="theme-color" content="#ffffff" />
                <link rel="canonical" href={canonicalURL} />

                {/* Add Structured Data if it exists */}
                {pageProps?.structuredData && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(pageProps.structuredData),
                        }}
                    />
                )}
            </Head>
            <Script
                id="openai-pixel-stub"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `!function(w){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q}(window);oaiq("init",{pixelId:"PZhyTxN6bqdu4QJg5YKWGn"});`,
                }}
            />
            <Script
                id="openai-pixel-sdk"
                src="https://bzrcdn.openai.com/sdk/oaiq.min.js"
                strategy="afterInteractive"
                onLoad={() => {
                    if (!window.location.hostname.includes('secretfoodtours.com')) return;
                    window.oaiq('measure', 'page_viewed', {
                        type: 'contents',
                        contents: [{
                            id: window.location.pathname,
                            name: document.title,
                            content_type: 'page',
                        }],
                    });
                }}
            />
            <Script id="gtm-inline-script"
            dangerouslySetInnerHTML={{
                __html: `
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(function () {
                    loadGTM();
                    });
                } else {
                    setTimeout(loadGTM, 3000);
                }
                function loadGTM() {
                    (function(w, d, s, l, i) {
                    w[l] = w[l] || [];
                    w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
                    var f = d.getElementsByTagName(s)[0],
                        j = d.createElement(s),
                        dl = l != 'dataLayer' ? '&l=' + l : '';
                    j.async = true;
                    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                    f.parentNode.insertBefore(j, f);
                    })(window, document, 'script', 'dataLayer', 'GTM-PMM7N5R');
                }
                `,
            }}
            />
            <ThemeProvider theme={lightTheme}>
                <GlobalStyles />
                <PopupScript/>
                {/*<div*/}
                {/*  id="embed_widget"*/}
                {/*  data-th-embed*/}
                {/*  data-th-config='{"widgetId":"a89a90d7-dcca-419e-8fcf-b09991a96dac"}'*/}
                {/*  style={{ minHeight: '600px', display: 'block' }}*/}
                {/*/>*/}
                <Script
                  id="th-widget"
                  src="https://widget.ticketinghub.com/embedding/latest.js"
                  strategy="afterInteractive"
                  onLoad={() => {
                    try {
                      const el = document.getElementById('embed_widget');
                      if (el && window.TicketinghubWidget) {
                        const already = el.querySelector('iframe#ticketinghub-embed-iframe');
                        if (!already) {
                          const cfg = window.TicketinghubWidget.widgetConfig(el);
                          new window.TicketinghubWidget(el, cfg);
                        }
                      }
                    } catch (e) {
                      console.error('TicketingHub init failed', e);
                    }
                  }}
                />
                <Component {...pageProps} />
                {/*<Script*/}
                {/*  src="https://widget.ticketinghub.com/embedding/latest.js"*/}
                {/*  strategy="afterInteractive"*/}
                {/*/>*/}
            </ThemeProvider>
        </Provider>
    );
}

export default App;
